/**
 * @file apiCallWithToast.ts
 * @module utils/apiCallWithToast
 * @description Shared wrapper for admin API calls that maps ResponseMessage
 *   status (info | success | warning | error) to the matching toast, and
 *   returns the payload when the call is not a hard error.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.5
 * @version 2.1.2
 */

import APICallService from "../api/apiCallService";
import {
  ApiResponseToastItem,
  ApiResponseToastStatus,
  showToasterFromApiResponse,
} from "./apiResponseToast";
import { showSilentResponseNotification } from "src/app/modules/_common/utils/api-helper";

type RequestOptions = {
  endpoint: string;
  params?: Record<string, any>;
  module?: any;
  warehouse?: string;
  onSuccess?: (response: any) => void;
  /**
   * When true, also show a toast for `status: "success"`.
   * Info / warning / error always toast from the server message.
   * Default false so quiet GETs (e.g. settings fetch) stay silent on success.
   */
  toastOnSuccess?: boolean;
  path?: string;
  type?: string;
  lang?: string;
  isDelete?: boolean;
};

const isResponseMessage = (
  value: any,
): value is ApiResponseToastItem & { status: string } =>
  !!value &&
  typeof value === "object" &&
  typeof value.status === "string" &&
  typeof value.message === "string";

export const callApiWithToast = async ({
  endpoint,
  params = {},
  module,
  warehouse,
  onSuccess,
  toastOnSuccess = false,
  path = "",
  type = "application/json",
  lang = "en",
  isDelete = false
}: RequestOptions) => {
  const apiService = new APICallService(
    endpoint,
    params,
    path,
    type,
    lang,
    isDelete,
    module,
    warehouse,
  );

  try {
    const response = await apiService.callAPI();
    console.trace(response);

    if (!response || response === 0) {
      return null;
    }

    if (isResponseMessage(response)) {
      const status = response.status as ApiResponseToastStatus;
      const shouldToast =
        status === "error" ||
        status === "warning" ||
        status === "info" ||
        (status === "success" && toastOnSuccess);

      if (shouldToast) {
        showToasterFromApiResponse(response, showSilentResponseNotification);
      }

      if (status === "error" || status === "warning" || status === "info") {
        return null;
      }

      onSuccess?.(response);
      return response;
    }

    onSuccess?.(response);
    return response;
  } catch (error: any) {
    const data = error?.response?.data;
    if (isResponseMessage(data)) {
      showToasterFromApiResponse(data, showSilentResponseNotification);
      return data.status === "error" ? null : data;
    }

    showSilentResponseNotification(
      "error",
      error?.message || "Something went wrong. Please try again.",
    );
    return null;
  }
};
